import { useState } from 'react'
import { Linking, Text, TextInput, View } from 'react-native'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Receipt } from '@/components/ui/icons'
import { Button } from '@/components/ui/Button'
import { GlassSheet } from '@/components/ui/GlassSheet'
import {
  disputeHostGatewayFee,
  getHostGatewayFeeClaim,
} from '@/lib/services/host-gateway-fee.service'
import { useTheme } from '@/lib/hooks/use-theme'
import { useSemanticColors, SPACING, RADIUS } from '@/lib/theme/tokens'
import { FONTS } from '@/lib/brand'
import { formatCurrency } from '@/lib/utils/format'
import { captureError } from '@/lib/utils/observability'
import { toast } from '@/lib/toast'

export function HostGatewayFeeCard({ rentalId }: { rentalId: string }) {
  const { t, i18n } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const queryClient = useQueryClient()
  const queryKey = ['host-gateway-fee', rentalId] as const
  const {
    data: claim,
    isError,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: () => getHostGatewayFeeClaim(rentalId),
    staleTime: 15_000,
    retry: 1,
  })
  const [editing, setEditing] = useState(false)
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)

  if (!claim && !isError) return null
  const canDispute =
    claim?.status === 'notice_period' && new Date(claim.disputeUntil).getTime() > Date.now()
  const pendingCents = claim ? Math.max(0, claim.actualCostCents - claim.appliedCents) : 0
  const displayCents =
    claim?.status === 'waived'
      ? 0
      : claim?.status === 'approved'
        ? pendingCents
        : (claim?.actualCostCents ?? 0)
  const disputeDate = claim?.disputeUntil
    ? new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone: 'America/El_Salvador',
      }).format(new Date(claim.disputeUntil))
    : ''

  const submitDispute = async () => {
    if (!canDispute || saving || !reason.trim()) return
    setSaving(true)
    try {
      await disputeHostGatewayFee(rentalId, reason)
      await queryClient.invalidateQueries({ queryKey })
      setEditing(false)
      setReason('')
      toast.success(t('host_gateway_fee.dispute_saved'))
    } catch (error) {
      captureError(error, { component: 'HostGatewayFeeCard', action: 'dispute' })
      toast.error(t('host_gateway_fee.dispute_error'))
      void refetch()
    } finally {
      setSaving(false)
    }
  }

  const requestStatement = async () => {
    try {
      await Linking.openURL(
        `mailto:teamtagorent@gmail.com?subject=${encodeURIComponent(`Comprobante Wompi ${rentalId}`)}`,
      )
    } catch (error) {
      captureError(error, { component: 'HostGatewayFeeCard', action: 'request-statement' })
      toast.error(t('host_gateway_fee.statement_error'))
    }
  }

  return (
    <GlassSheet
      testID="host-gateway-fee.card"
      style={{ marginTop: SPACING.lg }}
      contentStyle={{ padding: SPACING.lg, gap: SPACING.sm }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
        <Receipt size={21} color={colors.textAccent} />
        <Text
          style={{ flex: 1, color: colors.textPrimary, fontFamily: FONTS.bodyBold, fontSize: 17 }}
        >
          {t('host_gateway_fee.title')}
        </Text>
      </View>
      {isError || !claim ? (
        <>
          <Text style={{ color: colors.textSecondary, fontFamily: FONTS.body, fontSize: 14 }}>
            {t('host_gateway_fee.load_error')}
          </Text>
          <Button
            title={t('common.retry')}
            variant="secondary"
            onPress={() => void refetch()}
            testID="host-gateway-fee.retry"
          />
        </>
      ) : (
        <>
          <Text
            style={{ color: colors.textPrimary, fontFamily: FONTS.bodyBold, fontSize: 27 }}
            testID="host-gateway-fee.amount"
          >
            {formatCurrency(displayCents)}
          </Text>
          {claim.status === 'approved' && claim.appliedCents > 0 && (
            <Text style={{ color: colors.textSecondary, fontFamily: FONTS.body, fontSize: 13 }}>
              {t('host_gateway_fee.partially_applied', {
                applied: formatCurrency(claim.appliedCents),
                pending: formatCurrency(pendingCents),
              })}
            </Text>
          )}
          <Text
            style={{
              color: colors.textSecondary,
              fontFamily: FONTS.body,
              fontSize: 14,
              lineHeight: 21,
            }}
          >
            {t(`host_gateway_fee.status_${claim.status}`)}
          </Text>
          <View
            style={{
              borderTopWidth: 1,
              borderColor: colors.border,
              paddingTop: SPACING.sm,
              gap: 4,
            }}
          >
            <Text style={{ color: colors.textSecondary, fontFamily: FONTS.body, fontSize: 13 }}>
              {t('host_gateway_fee.breakdown', {
                gross: formatCurrency(claim.grossFeeCents),
                credit: formatCurrency(claim.processorCreditCents),
              })}
            </Text>
            <Text
              selectable
              style={{ color: colors.textMuted, fontFamily: FONTS.body, fontSize: 12 }}
            >
              {t('host_gateway_fee.reference', { reference: claim.statementReference })}
            </Text>
          </View>
          {canDispute && (
            <>
              <Text
                style={{
                  color: colors.textSecondary,
                  fontFamily: FONTS.body,
                  fontSize: 13,
                  lineHeight: 19,
                }}
              >
                {t('host_gateway_fee.dispute_until', { date: disputeDate })}
              </Text>
              {!editing ? (
                <Button
                  title={t('host_gateway_fee.dispute')}
                  variant="secondary"
                  size="lg"
                  onPress={() => setEditing(true)}
                  testID="host-gateway-fee.open-dispute"
                />
              ) : (
                <View style={{ gap: SPACING.sm }}>
                  <TextInput
                    value={reason}
                    onChangeText={setReason}
                    placeholder={t('host_gateway_fee.reason_placeholder')}
                    placeholderTextColor={colors.textMuted}
                    accessibilityLabel={t('host_gateway_fee.reason_label')}
                    testID="host-gateway-fee.reason"
                    multiline
                    maxLength={2000}
                    textAlignVertical="top"
                    style={{
                      minHeight: 96,
                      borderRadius: RADIUS.md,
                      borderWidth: 1,
                      borderColor: colors.border,
                      backgroundColor: colors.surface,
                      padding: SPACING.md,
                      color: colors.textPrimary,
                      fontFamily: FONTS.body,
                      fontSize: 15,
                    }}
                  />
                  <Button
                    title={t('host_gateway_fee.send_dispute')}
                    size="lg"
                    loading={saving}
                    disabled={!reason.trim() || saving}
                    onPress={() => void submitDispute()}
                    testID="host-gateway-fee.submit"
                  />
                  <Button
                    title={t('common.cancel')}
                    variant="text"
                    onPress={() => {
                      setEditing(false)
                      setReason('')
                    }}
                  />
                </View>
              )}
            </>
          )}
          <Button
            title={t('host_gateway_fee.request_statement')}
            variant="text"
            onPress={() => void requestStatement()}
            testID="host-gateway-fee.statement"
          />
        </>
      )}
    </GlassSheet>
  )
}
